import React, { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import confetti from 'canvas-confetti'; // Import canvas-confetti
import VerseContent from './VerseFetcher/VerseFetcher';
import CompletionDialog from './CompletionDialog';
import {
  calculateStreakFromDates,
  calculateProgress,
} from '../utils/streakUtils';
import { userService } from '../services/user.service';
import {
  getCompletedDaysForYear,
  getCompletedDayCompletions,
  toggleDayCompletion,
} from '../services/user.utils';
import { CongratulationsService } from '../services/congratulations.service';
import { BibleVersion } from '../models';

interface DailyCardProps {
  dayOfYear: number;
  readings: string[];
  version: BibleVersion;
  fontSize: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  setIsRead: (isRead: boolean) => void;
  isRead: boolean;
  currentDayKey: string;
}

const DailyCard: React.FC<DailyCardProps> = ({
  readings,
  version,
  fontSize,
  setIsRead,
  isRead,
  currentDayKey,
}) => {
  const [isLoading, setIsLoading] = useState(false); // New state for loading indicator
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);
  const [streak, setStreak] = useState(0);
  const [progress, setProgress] = useState(0);
  const [congratulationsMessage, setCongratulationsMessage] = useState<{
    verseContent: string;
    reference?: string | undefined;
  }>({ verseContent: '', reference: undefined });
  const [dialogStreak, setDialogStreak] = useState(0);
  const [dialogProgress, setDialogProgress] = useState(0);
  const auth = getAuth();
  const user = auth.currentUser;

  const TOTAL_DAYS = 365; // Total days in the reading plan

  // Fetch user read data and calculate streak/progress
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) return;

      try {
        const completions = await getCompletedDayCompletions(user.uid, '2026');
        const completedDays = completions.map((c) => c.dayNumber);

        const currentDayNumber = parseInt(
          currentDayKey.replace('2026-day-', '')
        );
        const currentStreak = calculateStreakFromDates(completions);
        const currentProgress = calculateProgress(completedDays, TOTAL_DAYS);

        setStreak(currentStreak);
        setProgress(currentProgress);
        setDialogStreak(currentStreak);
        setDialogProgress(currentProgress);
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchUserData();
  }, [user, currentDayKey]);

  const toggleReadStatus = async () => {
    if (!user) return;

    setIsLoading(true); // Set loading state to true

    try {
      const match = currentDayKey.match(/^(\d{4})-day-(\d+)$/);
      if (match) {
        const year = match[1];
        const dayNumber = parseInt(match[2], 10);
        await toggleDayCompletion(user.uid, dayNumber, year);
      }

      setIsRead(!isRead);

      if (!isRead) {
        // Recalculate streak and progress with the updated data
        try {
          const completions = await getCompletedDayCompletions(
            user.uid,
            '2026'
          );
          const completedDays = completions.map((c) => c.dayNumber);
          const currentDayNumber = parseInt(
            currentDayKey.replace('2026-day-', '')
          );
          const updatedStreak = calculateStreakFromDates(completions);
          const updatedProgress = calculateProgress(completedDays, TOTAL_DAYS);

          setStreak(updatedStreak);
          setProgress(updatedProgress);
          setDialogStreak(updatedStreak);
          setDialogProgress(updatedProgress);
        } catch (error) {
          console.error('Error recalculating progress:', error);
        }

        // Fetch congratulations message with a Bible verse using user's preferred version
        try {
          const message =
            await CongratulationsService.getRandomCongratulationsVerse(version);
          setCongratulationsMessage(message);
        } catch (error) {
          console.error('Error fetching congratulations message:', error);
          setCongratulationsMessage({
            verseContent: 'Scripture is the foundation of faith.',
            reference: undefined,
          });
        }

        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
        });

        // Show completion dialog after marking as read
        setTimeout(() => {
          setShowCompletionDialog(true);
        }, 1000); // Small delay to let confetti finish
      }
    } catch (error) {
      console.error('Error updating read status:', error);
    } finally {
      setIsLoading(false); // Set loading state to false
    }
  };

  return (
    <div className='flex items-center justify-center bg-gradient-to-r from-blue-400 to-blue-600 p-4 sm:p-8'>
      <div className='w-full max-w-lg bg-white rounded-lg shadow-xl'>
        <div className='p-6 sm:p-8'>
          <VerseContent
            verseReference={readings}
            version={version}
            size={fontSize}
          />
          <div className='flex justify-center mb-6'>
            <button
              onClick={toggleReadStatus}
              className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition duration-300 ease-in-out transform hover:scale-110 ${
                isRead
                  ? 'bg-green-500 border-green-700'
                  : 'bg-gray-300 border-gray-500'
              }`}
              disabled={isLoading} // Disable button when loading
            >
              {isLoading ? (
                <svg
                  className='animate-spin h-5 w-5 text-white'
                  xmlns='http://www.w3.org/2000/svg'
                  fill='none'
                  viewBox='0 0 24 24'
                >
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='4'
                  ></circle>
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z'
                  ></path>
                </svg>
              ) : isRead ? (
                '✔️'
              ) : (
                ''
              )}
            </button>
          </div>
          <div className='text-center'>
            <p className='text-sm text-gray-500'>
              {isRead
                ? 'You have already read for this day. Great job!'
                : "Mark this day as read when you've completed your reading."}
            </p>
          </div>
        </div>
      </div>

      <CompletionDialog
        open={showCompletionDialog}
        onClose={() => setShowCompletionDialog(false)}
        streak={dialogStreak}
        progress={dialogProgress}
        totalDays={TOTAL_DAYS}
        congratulationsMessage={congratulationsMessage}
      />
    </div>
  );
};

export default DailyCard;
